import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { omitQueryKeys } from '@rhg/utils/routes';
import { clearSAMLStorage } from '@rhg/utils/sso';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback } from 'react';

export const RegistrationDialogOpenQueryKey = 'rdo';

export function useRegistrationDialog() {
    const { query, replace, pathname } = useRouter();

    const isRegistrationDialogOpen =
        query[RegistrationDialogOpenQueryKey] === 'true';
    const registrationDialogFlow = query.registrationFlow as RegistrationFlow;
    const registrationDialogStep = query.currentStep as RegistrationFlowStep;

    const showRegistrationDialog = useCallback(
        async (props?: {
            currentStep?: RegistrationFlowStep;
            registrationFlow?: RegistrationFlow;
            forcedQuery?: ParsedUrlQuery;
            forcedPathname?: string;
            dropQueryKeys?: string[];
        }) => {
            const baseQuery = props?.dropQueryKeys
                ? omitQueryKeys(query, props.dropQueryKeys)
                : query;
            return replace(
                {
                    pathname: props?.forcedPathname || pathname,
                    query: {
                        ...baseQuery,
                        ...props?.forcedQuery,
                        [RegistrationDialogOpenQueryKey]: 'true',
                        currentStep:
                            props?.currentStep ??
                            RegistrationFlowStep.AccessCode,
                        registrationFlow:
                            props?.registrationFlow ??
                            RegistrationFlow.ExistingRegistration,
                    },
                },
                undefined,
                { shallow: true }
            );
        },
        [pathname, query, replace]
    );

    const closeRegistrationDialog = useCallback(async () => {
        if (!isRegistrationDialogOpen) {
            return;
        }
        clearSAMLStorage();
        return replace(
            {
                pathname,
                query: omitQueryKeys(query, [
                    RegistrationDialogOpenQueryKey,
                    'currentStep',
                    'registrationFlow',
                ]),
            },
            undefined,
            { shallow: true }
        );
    }, [isRegistrationDialogOpen, pathname, query, replace]);

    return {
        showRegistrationDialog,
        closeRegistrationDialog,
        isRegistrationDialogOpen,
        registrationDialogFlow,
        registrationDialogStep,
    };
}
